/**
 * Display of server instants (roadmap §3.2, sub-task 3.2). Every timestamp is shown in Asia/Bangkok,
 * whatever the device's own zone, in the person's calendar: Buddhist Era (BE, the Thai default) or
 * Common Era (CE). Digits are always Latin. Shared by the PWA and the Worker (push and Discord texts), so a
 * deadline reads the same in the app, in a notification and in the student channel.
 */
export const DISPLAY_TIME_ZONE = 'Asia/Bangkok';

export type DisplayLocale = 'th' | 'en';
export type DisplayCalendar = 'be' | 'ce';

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(locale: DisplayLocale, calendar: DisplayCalendar, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const tag = `${locale === 'th' ? 'th-TH' : 'en-GB'}-u-ca-${calendar === 'be' ? 'buddhist' : 'gregory'}-nu-latn`;
  const key = `${tag}|${JSON.stringify(options)}`;
  let f = formatters.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(tag, { timeZone: DISPLAY_TIME_ZONE, hourCycle: 'h23', ...options });
    formatters.set(key, f);
  }
  return f;
}

const asDate = (instant: Date | string | number) => (instant instanceof Date ? instant : new Date(instant));

/** "27 ก.ย. 2569 23:30" · "27 Sept 2026, 23:30" */
export function formatDateTime(instant: Date | string | number, locale: DisplayLocale, calendar: DisplayCalendar): string {
  return formatter(locale, calendar, { dateStyle: 'medium', timeStyle: 'short' }).format(asDate(instant));
}

/** "วันอาทิตย์ที่ 27 กันยายน พ.ศ. 2569" · "Sunday, 27 September 2026" */
export function formatDateLong(instant: Date | string | number, locale: DisplayLocale, calendar: DisplayCalendar): string {
  return formatter(locale, calendar, { dateStyle: 'full' }).format(asDate(instant));
}

/** "27 ก.ย. 2569" · "27 Sept 2026" */
export function formatDate(instant: Date | string | number, locale: DisplayLocale, calendar: DisplayCalendar): string {
  return formatter(locale, calendar, { dateStyle: 'medium' }).format(asDate(instant));
}

/** "23:30" (24-hour clock, Bangkok time). */
export function formatTime(instant: Date | string | number, locale: DisplayLocale): string {
  return formatter(locale, 'ce', { hour: '2-digit', minute: '2-digit' }).format(asDate(instant));
}

/** The calendar year of an instant in Bangkok: 2569 (BE) or 2026 (CE). */
export function displayYear(instant: Date | string | number, calendar: DisplayCalendar): number {
  const ce = Number(formatter('en', 'ce', { year: 'numeric' }).format(asDate(instant)));
  return calendar === 'be' ? ce + 543 : ce;
}
